"use client";

/**
 * Vendor rationale — the labelled prose rows explaining why this vendor, why
 * now, and why it fits this organization.
 *
 * Extracted from the design source `compass-oppreport.jsx` (`rationale`
 * section): one `.rv-card.rv-rows` container holding a `.rv-row` per row,
 * each with a `.rv-rowtop` (the `.rv-rowname` label plus an optional
 * citation) and a `.rv-rowdesc` prose paragraph.
 *
 * Renders only the rows it is handed. Real data supplies "Why now" alone
 * today (plan §5 gap #11) — the component has no way to know that "Why this
 * vendor" and "Why it fits this organization" are the other two labels the
 * design expects, so it never synthesizes a placeholder row for a missing
 * one.
 */

import { ReportCitationMarker } from "./ReportCitationMarker";
import type { ReportRationaleRow } from "./report-types";

export interface ReportVendorRationaleProps {
  rows: ReportRationaleRow[];
  onOpenCitation?: (n: number) => void;
}

/**
 * `.rv-card.rv-rows` — the white bordered container. The `.rv-rows` variant
 * zeroes the card's own 18px padding and lets each `.rv-row` carry it, so the
 * dividers run edge to edge.
 */
const RV_CARD =
  "bg-(--bg-white) border border-(--border-subtle) rounded-(--radius-container) mb-3 overflow-hidden";

/** `.rv-row` — 16px/18px padding with a hairline rule above every row but the first. */
const RV_ROW =
  "px-[18px] py-4 border-t border-(--border-subtle) first:border-t-0";

/** `.rv-rowname` — 13.5px/500 muted label on a fixed 260px column. */
const RV_ROW_NAME =
  "flex-[0_0_260px] max-[860px]:flex-[0_0_160px] text-[13.5px] font-medium text-(--fg-muted)";

/**
 * The `rationale` report section: one card, one row per
 * `ReportRationaleRow`, each carrying its label, optional citation, and body
 * prose.
 */
export function ReportVendorRationale({
  rows,
  onOpenCitation,
}: ReportVendorRationaleProps) {
  if (rows.length === 0) {
    return (
      <p className="m-0 text-[12.5px] leading-[1.6] text-(--fg-muted)">
        No vendor rationale is captured on this opportunity yet.
      </p>
    );
  }

  return (
    <div className={RV_CARD}>
      {rows.map((row) => (
        <div key={row.id} className={RV_ROW}>
          {/* .rv-rowtop */}
          <div className="flex items-baseline gap-3">
            <span className={RV_ROW_NAME}>
              {row.label}
              {row.citation != null && (
                <ReportCitationMarker
                  n={row.citation}
                  onClick={onOpenCitation}
                />
              )}
            </span>
          </div>
          {/* .rv-rowdesc */}
          <p className="m-0 mt-1.5 text-[12.5px] leading-[1.5] text-(--fg-secondary)">
            {row.body}
          </p>
        </div>
      ))}
    </div>
  );
}
